import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkle, Brain, HeartHalf, Compass, ShieldCheck, Star, CaretDown } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Disclaimer } from "@/components/Disclaimer";
import { AssessmentIcon } from "@/lib/icons";
import api from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: "easeOut" },
};

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800" data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-4 py-5 text-left" data-testid={`faq-toggle-${idx}`}>
        <span className="font-heading text-base font-medium text-slate-100 sm:text-lg">{item.q}</span>
        <CaretDown size={18} className={`shrink-0 text-teal-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-5 pr-8 text-sm leading-relaxed text-slate-400">{item.a}</p>}
    </div>
  );
}

export default function Landing() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setNotFound(false);
    api.get(`/assessments/${slug}`).then(({ data }) => {
      setData(data);
      api.post("/events", { event: "landing_viewed", assessment_slug: slug }).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  const start = () => {
    api.post("/events", { event: "assessment_started", assessment_slug: slug }).catch(() => {});
    navigate(`/assessment/${slug}`);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-950"><Navbar />
        <div className="mx-auto max-w-xl px-5 pt-40 text-center">
          <h1 className="font-heading text-3xl text-slate-100">Assessment not found</h1>
          <Link to="/" className="mt-4 inline-block text-teal-400">← Back home</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950"><div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;
  }

  const c = data.landing;
  const accentText = data.color;
  const cta = (testid) => (
    <button onClick={start} className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-slate-950 transition-all duration-300 hover:shadow-[0_0_36px_rgba(20,184,166,0.45)]" style={{ background: data.color }} data-testid={testid}>
      {c.ctaText} <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img src={data.hero_image} alt="" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-36 sm:px-8 sm:pb-32 sm:pt-44">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur" style={{ color: data.color }}>
              <AssessmentIcon name={data.icon} size={14} weight="fill" /> {c.eyebrow}
            </span>
            <h1 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              {c.headline.map((line, i) => (
                <span key={i} className="block">{i === c.highlight ? <span className="font-medium text-glow-teal" style={{ color: accentText }}>{line}</span> : line}</span>
              ))}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">{c.subhead}</p>
            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              {cta("hero-cta-btn")}
              <span className="text-sm text-slate-500">Free · {data.duration_min} minutes · No paywall</span>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <span className="flex -space-x-1.5">{["#F472B6", "#38BDF8", "#FBBF24", "#A78BFA"].map((cc) => <span key={cc} className="h-6 w-6 rounded-full border-2 border-slate-950" style={{ background: cc }} />)}</span>
                Trusted by <span className="font-semibold text-slate-300">12,000+</span> self-explorers
              </span>
              <span className="flex items-center gap-1.5">{[...Array(5)].map((_, i) => <Star key={i} size={14} weight="fill" className="text-amber-400" />)}<span className="ml-1">Grounded in real psychology</span></span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="relative border-t border-slate-900 bg-slate-950 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">The Pattern</p>
              <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">{c.painTitle}</h2>
              <p className="mt-5 text-base leading-relaxed text-slate-400">The patterns running your life are mostly invisible to you — that&apos;s exactly why they keep running. This assessment makes them visible, so you can finally choose differently.</p>
            </div>
            <ul className="space-y-4">
              {c.pains.map((p, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-800 font-mono text-xs text-teal-400">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm leading-relaxed text-slate-300">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* DISCOVER */}
      <section className="bg-slate-950 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">What You&apos;ll Discover</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">Insight you can actually use.</h2>
          </motion.div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {c.discover.map((d, i) => {
              const Icon = [Brain, HeartHalf, Compass][i] || Sparkle;
              return (
                <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="rounded-xl border border-slate-800 bg-slate-900 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-slate-700">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-500/10 text-teal-400"><Icon size={24} weight="duotone" /></span>
                  <h3 className="mt-5 font-heading text-lg font-medium text-slate-100">{d.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{d.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESULT TYPES PREVIEW */}
      <section className="border-y border-slate-900 bg-gradient-to-b from-slate-950 to-slate-900/30 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Which one are you?</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">Your result will reveal your dominant type.</h2>
          </motion.div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.result_types.map((t, i) => (
              <motion.div key={t.key} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.07 }} className="group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-slate-700">
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-20 blur-2xl transition-opacity group-hover:opacity-40" style={{ background: t.color }} />
                <div className="flex items-center gap-3"><span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} /><h3 className="font-heading text-lg font-medium text-slate-100">{t.name}</h3></div>
                <p className="mt-3 text-sm italic text-slate-400">&quot;{t.tagline}&quot;</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="bg-slate-950 py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {c.testimonials.map((t, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }} className="rounded-xl border border-slate-800 bg-slate-900 p-6">
                <div className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} size={14} weight="fill" className="text-amber-400" />)}</div>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">&quot;{t.quote}&quot;</p>
                <p className="mt-4 text-xs font-medium uppercase tracking-wider text-slate-500">{t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-950 pb-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Questions</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">Before you begin</h2>
          </motion.div>
          <div className="mt-8">{c.faq.map((item, i) => <FaqItem key={i} item={item} idx={i} />)}</div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-slate-900 bg-slate-950 py-24 grain-overlay">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full blur-[120px]" style={{ background: `${data.color}1a` }} />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <ShieldCheck size={40} weight="duotone" style={{ color: data.color }} className="mx-auto" />
          <h2 className="mt-6 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">Your patterns are running you.<br /><span className="font-medium" style={{ color: data.color }}>Time to see them clearly.</span></h2>
          <div className="mt-9 flex justify-center">{cta("final-cta-btn")}</div>
          <div className="mx-auto mt-12 max-w-xl"><Disclaimer /></div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
